import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, catchError, map, of, throwError } from 'rxjs';
import type { ApiResponse } from '@shared-models';
import { API_CONFIG, type ApiConfig } from '../api-config';
import { mapMutationMessage } from '../../utils/map-pageable';

/** Response de firma digital del customer. */
export interface CustomerSignatureDto {
  id: string;
  customerId: string;
  signatureUrl: string;
  contentType?: string;
  fileSize?: number;
  source?: string;
  updatedAt?: string;
}

/**
 * Firma del cliente.
 *   GET    /api/customers/{customerId}/signature
 *   PUT    /api/customers/{customerId}/signature  (multipart: signature | file | signature.file)
 *   DELETE /api/customers/{customerId}/signature
 *
 * Roles: GET staff · PUT/DELETE ADMIN/MANAGER (enforced by backend).
 */
@Injectable({ providedIn: 'root' })
export class CustomerSignatureService {
  private readonly http = inject(HttpClient);
  private readonly apiConfig = inject<ApiConfig>(API_CONFIG);

  private resource(customerId: string): string {
    return `${this.apiConfig.apiUrl}/customers/${customerId}/signature`;
  }

  /**
   * Obtiene la firma. Si no existe (404), emite `null`.
   */
  get(customerId: string): Observable<CustomerSignatureDto | null> {
    return this.http
      .get<ApiResponse<CustomerSignatureDto>>(this.resource(customerId))
      .pipe(
        map((res) => res.data ?? null),
        catchError((err: HttpErrorResponse) => {
          if (err.status === 404) return of(null);
          return throwError(() => err);
        }),
      );
  }

  /**
   * Crea o actualiza la firma (multipart).
   * Part preferida: `signature` (PNG/JPEG ~200 B–2 MB).
   */
  upsert(customerId: string, file: File): Observable<CustomerSignatureDto> {
    const formData = new FormData();
    formData.append('signature', file, file.name || 'signature.png');
    return this.http
      .put<ApiResponse<CustomerSignatureDto>>(
        this.resource(customerId),
        formData,
      )
      .pipe(map((res) => res.data as CustomerSignatureDto));
  }

  delete(customerId: string): Observable<{ message: string }> {
    return this.http
      .delete<{ success?: boolean; message?: string }>(
        this.resource(customerId),
      )
      .pipe(
        map((response) =>
          mapMutationMessage(response, 'Firma eliminada exitosamente'),
        ),
      );
  }
}
